import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { IStudy } from "../../../@types/admin/AdminStudy";

dayjs.extend(utc);

/**
 * Legacy-CMS field helpers for the Studies page (EN-5155). `StudyResponseDto`
 * stringifies its bools and ints (`Publish = study.Publish?.ToString()` →
 * "True"/"False"), while the add/update DTOs take real `bool` / `int` — so
 * every read needs a parse and every write a conversion.
 */

/** `"True"` / `"true"` / `"1"` → true; anything else (including null) false. */
export function parseCmsBoolean(value: string | null | undefined): boolean {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized === "true" || normalized === "1";
}

/** `"12"` → 12; null / not-a-number → undefined. */
export function parseCmsNumber(
  value: string | null | undefined
): number | undefined {
  if (value === null || value === undefined || value.trim() === "") {
    return undefined;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

/**
 * A .NET `TimeSpan` (`"90.00:00:00"`, or `"12:00:00"` under a day) as a
 * readable span. Retool ran `moment.duration(item).humanize()`, which
 * rounds — "3 months" for 90 days — and an operator setting an expiry needs
 * the exact number of days, so this states them.
 */
export function formatStudyDuration(
  timeSpan: string | null | undefined
): string {
  if (!timeSpan) return "-";

  const [daysPart, clockPart] = timeSpan.includes(".")
    ? [
        timeSpan.slice(0, timeSpan.indexOf(".")),
        timeSpan.slice(timeSpan.indexOf(".") + 1),
      ]
    : ["0", timeSpan];

  const days = Number.parseInt(daysPart, 10);
  if (Number.isNaN(days)) return "-";

  if (days > 0) return days === 1 ? "1 day" : `${days} days`;

  const hours = Number.parseInt(clockPart.split(":")[0] ?? "", 10);
  if (Number.isNaN(hours)) return "-";
  if (hours <= 0) return "Same day";
  return hours === 1 ? "1 hour" : `${hours} hours`;
}

/**
 * Days between an expiry date and the study's start — what the API's
 * `DurationInDays` means.
 *
 * Retool computed this differently per mode: `diff(now)` when creating and
 * `diff(created)` when editing. That is not two rules but one — the duration
 * is measured from the row's start date, and a new row starts now — so the
 * caller passes the start and this does the arithmetic once.
 *
 * Both ends are read as UTC calendar days. Two reasons, and skipping either
 * loses a day:
 *
 * - A raw `diff(..., "day")` truncates the part-day, so a 30-day expiry came
 *   out as 29 once the clock passed the created time of day. Hence
 *   `startOf("day")` on both sides.
 * - The API stores and returns UTC (`expireAt` = `created` + `duration`), and
 *   the `<input type="date">` value is a bare calendar date. Reading either
 *   through the viewer's local zone shifts the day for anyone west of UTC —
 *   which is what `toDateInputValue` guards on the way out.
 */
export function durationInDaysFrom(
  startDate: string | null | undefined,
  expiryDate: string
): number {
  const start = startDate ? dayjs.utc(startDate) : dayjs.utc();
  return dayjs.utc(expiryDate).startOf("day").diff(start.startOf("day"), "day");
}

/**
 * `expireAt` as the `yyyy-MM-dd` an `<input type="date">` expects, in UTC.
 *
 * Formatting in local time would render a UTC-midnight expiry as the previous
 * day west of UTC, and the form would then post a duration one day short of
 * what it displayed. Same reason `getShortFormattedUtcDate` exists for dates
 * of birth.
 */
export function toDateInputValue(isoDate: string | null | undefined): string {
  if (!isoDate) return "";
  const parsed = dayjs.utc(isoDate);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

/** Ward + condition ids a study form starts from, parsed out of the row. */
export function studyFormDefaults(study: IStudy | undefined) {
  return {
    name: study?.name ?? "",
    code: study?.code ?? "",
    expireAt: toDateInputValue(study?.expireAt),
    hospitalId: study?.hospitalId ?? "",
    conditionTypeId: study?.conditionTypeId ?? "",
    // Retool's create form left Publish off; the API defaults it on. A study
    // nobody can sign up to is the safer default for a new signup code.
    publish: study ? parseCmsBoolean(study.publish) : false,
  };
}
